import { anyPass, equals } from 'ramda';

import { optimisticTranslate } from '@i18n';

import { TransactionsErrorCode } from '@framework/constants/enums/transaction';

import { getTransactionThrottleErrorMessage } from '@accounts/store/utils/transactions';
import { AuthenticationErrorCode, AuthenticationStep } from '@accounts/types/authentication';
import { type AuthenticationError } from '@accounts/types/store';

export const isOneTouchProofStep = equals(AuthenticationStep.OneTouchProof);
export const isTotpProofStep = equals(AuthenticationStep.TotpProof);
export const isMtanProofStep = equals(AuthenticationStep.MtanProof);
export const isMtanEnrollStep = equals(AuthenticationStep.MtanEnroll);
export const isSecondaryProofSelectorStep = equals(AuthenticationStep.SecondaryProofSelector);
export const isSecondaryProofAuthenticationStep = anyPass([
    isOneTouchProofStep,
    isTotpProofStep,
    isMtanProofStep
]);
export const isSecondaryProofStep = anyPass([
    isSecondaryProofAuthenticationStep,
    isSecondaryProofSelectorStep
]);

export const getErrorTitleByCode = (error: AuthenticationError): string | undefined => {
    let title: string | undefined;

    switch (error.code) {
        case AuthenticationErrorCode.ThrottleHours:
        case AuthenticationErrorCode.ThrottleMinutes:
        case AuthenticationErrorCode.ThrottleSeconds:
            title = optimisticTranslate('Too many requests');
            break;
        case TransactionsErrorCode.TransactionExpired:
            title = optimisticTranslate('Session expired');
            break;
        default:
            break;
    }

    return title;
};

export const getErrorButtonLabelByCode = (error: AuthenticationError): string | undefined => {
    let label: string | undefined;

    switch (error.code) {
        case TransactionsErrorCode.TransactionExpired:
            label = optimisticTranslate('Restart registration');
            break;
        default:
            break;
    }

    return label;
};

export const getErrorMessageByCode = (error: AuthenticationError): string => {
    const ERROR_MESSAGES_BY_CODE: { [key: string]: string } = {
        'account-inactive': optimisticTranslate('Account is inactive.'),
        [TransactionsErrorCode.InvalidTransaction]: optimisticTranslate(
            "We're sorry about this. To join your new room on Sherpany, please continue with your invitation."
        ),
        [TransactionsErrorCode.TransactionExpired]: optimisticTranslate(
            'Your session has expired. Please start your registration again to accept your invitation.'
        ),
        [TransactionsErrorCode.Throttled]: getTransactionThrottleErrorMessage(error?.params?.wait),
        [TransactionsErrorCode.ErrorProcessingRequest]: optimisticTranslate(
            `Previous passwords can't be reused. Please choose a different password.`
        ),
        [AuthenticationErrorCode.InvalidEmail]: optimisticTranslate(
            'Please enter a valid email address.'
        ),
        [AuthenticationErrorCode.InvalidPassword]: optimisticTranslate(
            'Please limit your password to maximum {{ length }} characters.',
            { length: 64 }
        ),
        [AuthenticationErrorCode.InvalidEmailOrPassword]: optimisticTranslate(
            'You’ve entered the wrong email or password. Please try again.'
        ),
        [AuthenticationErrorCode.ThrottleSeconds]: optimisticTranslate(
            'We have received too many requests. Please wait and try again in {{ seconds }} second(s).',
            error.params
        ),
        [AuthenticationErrorCode.ThrottleMinutes]: optimisticTranslate(
            'We have received too many requests. Please wait and try again in {{ minutes }} minute(s).',
            error.params
        ),
        [AuthenticationErrorCode.ThrottleHours]: optimisticTranslate(
            'We have received too many requests. Please wait and try again in {{ hours }} hour(s).',
            error.params
        ),
        [AuthenticationErrorCode.WrongPassword]: optimisticTranslate(
            'You’ve entered the wrong email or password. Please try again.'
        ),
        [AuthenticationErrorCode.Empty2faMethods]: optimisticTranslate(
            'Your organisation settings doesn’t allow you to login using this method. You may still be able to login with SSO if it’s configured for your organisation.'
        ),
        [AuthenticationErrorCode.WrongMtanCode]: optimisticTranslate(
            'The code is wrong. Please enter the security code that was sent to your phone.'
        ),
        [AuthenticationErrorCode.WrongTotpCode]: optimisticTranslate(
            'The code is wrong. Please enter the TOTP code from your Futurae app. Note that the code changes every 30s.'
        ),
        [AuthenticationErrorCode.SSOEmptyMethods]: optimisticTranslate(
            'We could not find any SSO provider based on that email address.'
        ),
        [AuthenticationErrorCode.NoUsableDeviceEnrolled]: optimisticTranslate(
            'Something went wrong. Please try again.'
        )
    };

    return ERROR_MESSAGES_BY_CODE[error.code];
};
